import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { activity, items } from '@/api/endpoints';
import { AuthedImage } from '@/components/AuthedImage';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EmptyLocationsCta } from '@/components/EmptyLocationsCta';
import { CategoryBreakdown } from '@/components/CategoryBreakdown';
import { RecentItems } from '@/components/RecentItems';
import { WarrantyExpiringCard } from '@/components/WarrantyExpiringCard';
import { Boxes, Home, ImageOff, Tags, Wallet } from 'lucide-react';
import type { CurrencyTotal } from '@/types/api';

export function DashboardPage() {
  const { t, i18n } = useTranslation();
  const stats = useQuery({ queryKey: ['stats'], queryFn: items.stats });
  const recent = useQuery({ queryKey: ['activity', 10], queryFn: () => activity.recent(10) });

  const fmtCurrency = (n: number, ccy: string | null) => {
    if (!ccy) return n.toLocaleString();
    try {
      return new Intl.NumberFormat(i18n.language, { style: 'currency', currency: ccy }).format(n);
    } catch {
      return `${n} ${ccy}`;
    }
  };

  const renderTotalValue = (currencyTotals: CurrencyTotal[]) => {
    if (currencyTotals.length === 0) return fmtCurrency(0, null);
    if (currencyTotals.length === 1) {
      const [{ total, currency }] = currencyTotals;
      return fmtCurrency(total, currency);
    }
    return (
      <div className="space-y-0.5 text-base">
        {currencyTotals.map((ct, i) => (
          <div key={i}>{fmtCurrency(ct.total, ct.currency)}</div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">{t('dashboard.title')}</h1>
      {stats.data && stats.data.totalLocations === 0 && <EmptyLocationsCta />}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
        <Link to="/items" className="block h-full rounded-lg transition-shadow hover:shadow-md">
          <Card className="h-full">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm">{t('dashboard.totalItems')}</CardTitle>
              <Boxes className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.data?.totalItems ?? '–'}</div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/locations" className="block h-full rounded-lg transition-shadow hover:shadow-md">
          <Card className="h-full">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm">{t('dashboard.totalLocations')}</CardTitle>
              <Home className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.data?.totalLocations ?? '–'}</div>
            </CardContent>
          </Card>
        </Link>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">{t('dashboard.totalCategories')}</CardTitle>
            <Tags className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.data?.totalCategories ?? '–'}</div>
          </CardContent>
        </Card>
        <WarrantyExpiringCard count={stats.data?.warrantyExpiringSoon} expiringItems={stats.data?.warrantyExpiringItems ?? []} />
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">{t('dashboard.totalValue')}</CardTitle>
            <Wallet className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.data ? renderTotalValue(stats.data.currencyTotals) : '–'}
            </div>
          </CardContent>
        </Card>
      </div>
      <RecentItems />
      <CategoryBreakdown />
      <Card>
        <CardHeader>
          <CardTitle>{t('dashboard.recentActivity')}</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="divide-y">
            {(recent.data?.content ?? []).map((a) => (
              <li key={a.id} className="flex items-center gap-3 py-3 text-sm">
                {a.thumbnailPath ? (
                  <AuthedImage src={a.thumbnailPath} alt="" className="h-8 w-8 shrink-0 rounded object-cover" />
                ) : (
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-muted" aria-hidden="true">
                    <ImageOff className="h-4 w-4 text-muted-foreground" />
                  </div>
                )}
                <span className="min-w-0 flex-1">
                  {t('activity.line', {
                    user: a.userEmail ?? '—',
                    action: t(`activity.actions.${a.action}`),
                    entity: t(`activity.entities.${a.entityType}`, { defaultValue: a.entityType }),
                    name: a.summary ?? '',
                  })}
                </span>
                <span className="shrink-0 text-xs text-muted-foreground">{new Date(a.occurredAt).toLocaleString()}</span>
              </li>
            ))}
            {recent.data && recent.data.content.length === 0 && (
              <li className="py-6 text-center text-sm text-muted-foreground">{t('common.noData')}</li>
            )}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
